// One saved-spot tile rendered above the active editor. Shows the spot's
// placement, content summary, and per-spot surcharge. Edit pulls the spot
// back into the editor (parent removes from spots[] + repopulates draft).
// Remove drops the spot.

import type { Config } from "../types";
import { spotCentsFor, type Spot } from "../pricing";

export function SavedSpotTile({
  spot,
  index,
  config,
  otherSpots,
  onEdit,
  onRemove,
}: {
  spot: Spot;
  index: number;
  config: Config;
  otherSpots: Spot[];
  onEdit: () => void;
  onRemove: () => void;
}) {
  const placement = config.placements.find((p) => p.slug === spot.placementSlug);
  const placementName = placement?.name ?? spot.placementSlug ?? "Unset";
  const cents = spotCentsFor(spot, config, otherSpots);
  // Pet-photo trap (2026-07-12): a photo-like upload the customer kept
  // as a plain logo (no Portrait upgrade) stitches simplified - say so
  // on the review line rather than letting the photo set expectations.
  const photoNote =
    spot.uploadPhotoLike && !spot.addonUpsellId
      ? " · stitched as a simplified logo"
      : "";
  // Upload-later: the artwork arrives after checkout via the upload
  // portal, so the tile says that instead of a blank "Design".
  const sendingLater = !!spot.uploadLater && !spot.uploadStagingKey;
  const designSummary = (() => {
    if (spot.mode === "logo") {
      if (sendingLater) return "Design · sending it after checkout";
      return (
        (spot.uploadFilename ? `Design · ${spot.uploadFilename}` : "Design") +
        photoNote
      );
    }
    if (spot.mode === "text") {
      const font = spot.fontId ? config.fonts.find((f) => f.id === spot.fontId) : null;
      const raw = spot.text.trim() || "(no text)";
      // Phase 32b: surface uppercase-only fonts as the customer will see
      // them stitched, so the saved-spot summary doesn't show "hello" in
      // a font that can only render "HELLO".
      const text = font?.forceUppercase ? raw.toUpperCase() : raw;
      return font ? `Text "${text}" · ${font.name}` : `Text "${text}"`;
    }
    if (spot.mode === "both") {
      const font = spot.fontId ? config.fonts.find((f) => f.id === spot.fontId) : null;
      const raw = spot.text.trim() || "(no text)";
      const text = font?.forceUppercase ? raw.toUpperCase() : raw;
      const filename = sendingLater
        ? "design (sending it after checkout)"
        : (spot.uploadFilename ?? "design");
      const arr = spot.arrangement ?? "right";
      const fontBit = font ? ` (${font.name})` : "";
      return `"${text}"${fontBit} ${arr} of ${filename}${photoNote}`;
    }
    return "Incomplete";
  })();

  const threadCodes = [...spot.textThreadCodes, ...spot.logoThreadCodes];
  const threadDots = threadCodes
    .map((code) => config.threads.find((t) => t.code === code))
    .filter((t): t is NonNullable<typeof t> => !!t);

  return (
    <div className="rk-pc-saved-spot">
      <div className="rk-pc-saved-spot__num">{index + 1}</div>
      <div className="rk-pc-saved-spot__body">
        <div className="rk-pc-saved-spot__title">{placementName}</div>
        <div className="rk-pc-saved-spot__sub">
          {designSummary} · {spot.sizeMm}mm
        </div>
        {threadDots.length > 0 && (
          <div className="rk-pc-saved-spot__threads" aria-label="Thread colours">
            {threadDots.map((t, i) => (
              <span
                key={`${t.code}-${i}`}
                className="rk-pc-saved-spot__thread"
                style={{ background: t.hex }}
                title={`${t.name} · Isacord ${t.code}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="rk-pc-saved-spot__price">
        {cents === 0 ? "Free" : `+$${(cents / 100).toFixed(0)}`}
      </div>
      <div className="rk-pc-saved-spot__actions">
        <button
          type="button"
          className="rk-pc-saved-spot__edit"
          onClick={onEdit}
          title="Edit this spot"
        >
          Edit
        </button>
        <button
          type="button"
          className="rk-pc-saved-spot__remove"
          onClick={onRemove}
          title="Remove this spot"
          aria-label="Remove this spot"
        >
          ×
        </button>
      </div>
    </div>
  );
}
